"use client"

import { signIn } from "next-auth/react"
import { useState } from "react"
import {
  Building2,
  Handshake,
  ArrowRight,
  Mail,
  Lock,
  Eye,
  EyeOff,
  User,
  MailCheck,
} from "lucide-react"
import { cn } from "@/lib/utils"

type UserRole = 'merchant' | 'partner'

export default function RegisterPage() {
  const [isLoading, setIsLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error, setError] = useState("")
  const [selectedRole, setSelectedRole] = useState<UserRole>('merchant')
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [registered, setRegistered] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [resendMessage, setResendMessage] = useState("")

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    if (password.length < 8) {
      setError("Password must be at least 8 characters.")
      return
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.")
      return
    }

    setIsLoading(true)
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password, role: selectedRole }),
      })

      const data = await res.json()

      if (!res.ok) {
        setError(data.error || "Registration failed. Please try again.")
        setIsLoading(false)
        return
      }

      setRegistered(true)
    } catch (err) {
      console.error('Register error:', err)
      setError("An unexpected error occurred. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = async () => {
    setResendLoading(true)
    setResendMessage("")
    try {
      await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      setResendMessage("Verification email resent. Please check your inbox.")
    } catch {
      setResendMessage("Failed to resend. Please try again.")
    } finally {
      setResendLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true)
    setError("")
    try {
      await signIn('google', { callbackUrl: '/', redirect: true })
    } catch (err) {
      console.error('Google sign-in error:', err)
      setError("An unexpected error occurred. Please try again.")
      setGoogleLoading(false)
    }
  }

  if (registered) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-[440px] relative z-10 text-center">
          <div className="flex justify-center mb-10">
            <div className="flex items-center gap-2">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src="/favicon.svg" alt="FluxPay" className="w-10 h-10" />
              <span className="font-semibold tracking-wide uppercase text-2xl hidden dark:inline" style={{ background: 'linear-gradient(135deg, #e0f4ff 0%, #ffffff 25%, #b0d8ff 50%, #ffffff 75%, #90b0e0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FluxPay</span>
              <span className="font-semibold tracking-wide uppercase text-2xl inline dark:hidden" style={{ background: 'linear-gradient(135deg, #0e7490 0%, #155e75 25%, #1D3557 50%, #0891b2 75%, #164e63 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FluxPay</span>
            </div>
          </div>
          <div className="p-8 rounded-3xl bg-card/80 dark:bg-white/[0.03] backdrop-blur-xl border border-border dark:border-white/[0.08] shadow-2xl">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                <MailCheck className="w-8 h-8 text-cyan-400" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">Check your email</h2>
            <p className="text-muted-foreground mb-2">
              We sent a verification link to
            </p>
            <p className="text-cyan-400 font-medium mb-6">{email}</p>
            <p className="text-muted-foreground text-sm mb-6">
              Click the link in the email to verify your account. The link expires in 24 hours.
            </p>
            {resendMessage && (
              <p className="text-sm text-green-400 mb-4">{resendMessage}</p>
            )}
            <button
              onClick={handleResend}
              disabled={resendLoading}
              className="text-sm text-muted-foreground hover:text-cyan-400 transition-colors disabled:opacity-50"
            >
              {resendLoading ? "Sending..." : "Resend verification email"}
            </button>
          </div>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already verified?{" "}
            <a href="/auth/signin" className="font-medium text-cyan-400 hover:text-cyan-300 transition-colors">
              Sign in
            </a>
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Subtle background gradient */}
      <div className="fixed inset-0 pointer-events-none">
        <div className={cn(
          "absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] rounded-full blur-[150px] transition-all duration-700",
          selectedRole === 'merchant' ? "bg-cyan-500/8" : "bg-purple-500/8"
        )} />
      </div>

      <div className="w-full max-w-[440px] relative z-10">
        {/* Logo */}
        <div className="flex justify-center mb-10">
          <div className="flex items-center gap-2">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/favicon.svg" alt="FluxPay" className="w-10 h-10" />
            <span className="font-semibold tracking-wide uppercase text-2xl hidden dark:inline" style={{ background: 'linear-gradient(135deg, #e0f4ff 0%, #ffffff 25%, #b0d8ff 50%, #ffffff 75%, #90b0e0 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FluxPay</span>
            <span className="font-semibold tracking-wide uppercase text-2xl inline dark:hidden" style={{ background: 'linear-gradient(135deg, #0e7490 0%, #155e75 25%, #1D3557 50%, #0891b2 75%, #164e63 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>FluxPay</span>
          </div>
        </div>

        {/* Role Selector */}
        <div className="flex justify-center mb-8">
          <div className="relative p-1.5 rounded-2xl bg-card/80 dark:bg-white/[0.03] border border-border dark:border-white/[0.08]">
            <div className={cn(
              "absolute top-1.5 bottom-1.5 w-[calc(50%-6px)] rounded-xl transition-all duration-500 ease-out",
              selectedRole === 'merchant'
                ? "left-1.5 bg-gradient-to-r from-cyan-500 to-cyan-600 shadow-lg shadow-cyan-500/30"
                : "left-[calc(50%+3px)] bg-gradient-to-r from-purple-500 to-purple-600 shadow-lg shadow-purple-500/30"
            )} />

            <div className="relative flex">
              <button
                type="button"
                onClick={() => setSelectedRole('merchant')}
                className={cn(
                  "flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 w-[140px]",
                  selectedRole === 'merchant' ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Building2 className="w-4 h-4" />
                <span>Merchant</span>
              </button>
              <button
                type="button"
                onClick={() => setSelectedRole('partner')}
                className={cn(
                  "flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 w-[140px]",
                  selectedRole === 'partner' ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Handshake className="w-4 h-4" />
                <span>Partner</span>
              </button>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2 tracking-tight">
            Create your account
          </h1>
          <p className="text-muted-foreground">
            {selectedRole === 'merchant'
              ? "Start accepting payments today"
              : "Join our partner program"}
          </p>
        </div>

        {/* Registration card */}
        <div className="relative p-8 rounded-3xl bg-card/80 dark:bg-white/[0.03] backdrop-blur-xl border border-border dark:border-white/[0.08] shadow-2xl">
          <div className={cn(
            "absolute inset-0 rounded-3xl opacity-50 transition-all duration-500",
            selectedRole === 'merchant'
              ? "bg-gradient-to-br from-cyan-500/10 via-transparent to-blue-500/10"
              : "bg-gradient-to-br from-purple-500/10 via-transparent to-pink-500/10"
          )} />

          <div className="relative">
            {error && (
              <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            {/* Registration Form */}
            <form onSubmit={handleRegister} className="space-y-4 mb-6">
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Full Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="w-full bg-gray-100 dark:bg-white/5 border border-border dark:border-white/10 text-foreground placeholder:text-muted-foreground focus:border-cyan-500 focus:outline-none rounded-xl h-12 pl-11 pr-4 text-sm transition-colors"
                />
              </div>

              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full bg-gray-100 dark:bg-white/5 border border-border dark:border-white/10 text-foreground placeholder:text-muted-foreground focus:border-cyan-500 focus:outline-none rounded-xl h-12 pl-11 pr-4 text-sm transition-colors"
                />
              </div>

              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password (min 8 characters)"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full bg-gray-100 dark:bg-white/5 border border-border dark:border-white/10 text-foreground placeholder:text-muted-foreground focus:border-cyan-500 focus:outline-none rounded-xl h-12 pl-11 pr-11 text-sm transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full bg-gray-100 dark:bg-white/5 border border-border dark:border-white/10 text-foreground placeholder:text-muted-foreground focus:border-cyan-500 focus:outline-none rounded-xl h-12 pl-11 pr-11 text-sm transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={cn(
                  "w-full h-12 flex items-center justify-center gap-2 rounded-xl font-semibold text-sm text-foreground transition-all duration-300 disabled:opacity-50 mt-2",
                  selectedRole === 'merchant'
                    ? "bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 shadow-lg shadow-cyan-500/25"
                    : "bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-400 hover:to-purple-500 shadow-lg shadow-purple-500/25"
                )}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Creating account...</span>
                  </>
                ) : (
                  <span>Create Account</span>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border dark:border-white/10" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-background text-muted-foreground">or continue with</span>
              </div>
            </div>

            {/* Google Sign In Button */}
            <button
              onClick={handleGoogleSignIn}
              disabled={googleLoading}
              className="brand-inverse-action group relative flex h-14 w-full items-center justify-center gap-3 rounded-2xl font-semibold shadow-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl active:scale-[0.98] disabled:opacity-50"
            >
              {googleLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  <span>Continue with Google</span>
                  <ArrowRight className="w-4 h-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                </>
              )}
            </button>
          </div>
        </div>

        {/* Sign in link */}
        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <a href="/auth/signin" className={cn(
            "font-medium transition-colors",
            selectedRole === 'merchant' ? "text-cyan-400 hover:text-cyan-300" : "text-purple-400 hover:text-purple-300"
          )}>
            Sign in
          </a>
        </p>
      </div>
    </div>
  )
}
